/**
 * Extract a user-friendly error message from a Supabase edge function response.
 *
 * The Supabase JS client wraps non-2xx responses in a `FunctionsHttpError` whose
 * `.message` is a generic "Edge Function returned a non-2xx status code". The
 * *real* error lives inside `error.context` (a Response object) and must be
 * parsed explicitly.
 *
 * Usage:
 *   const { data, error } = await supabase.functions.invoke(…);
 *   if (error || data?.error) {
 *     const msg = await extractEdgeFunctionError(error, data, "Something went wrong");
 *     throw new Error(msg);
 *   }
 */
export async function extractEdgeFunctionError(
  error: any,
  data: any,
  fallback = "Something went wrong. Please try again.",
): Promise<string> {
  // Append a stable error code (when the function returns one) so users can
  // quote it in support requests and we can grep the function logs for it.
  const withCode = (msg: string, code: unknown) =>
    typeof code === "string" && code ? `${msg} (${code})` : msg;

  // 1. If the function returned success but the body contains an error field
  if (data?.error) return typeof data.error === "string" ? withCode(data.error, data.code) : fallback;
  if (data?.errors?.[0]?.detail) return withCode(data.errors[0].detail, data.errors[0].code);

  // 2. Try to read the response body from the FunctionsHttpError context
  if (error?.context) {
    try {
      const body = await error.context.clone().json();
      if (body?.error) return typeof body.error === "string" ? withCode(body.error, body.code) : fallback;
      if (body?.message) return typeof body.message === "string" ? withCode(body.message, body.code) : fallback;
      if (body?.errors?.[0]?.detail) return withCode(body.errors[0].detail, body.errors[0].code);
    } catch {
      // context may have already been consumed or not be JSON
    }
  }

  // 3. Use the error message if it's meaningful (not the generic wrapper)
  if (
    error?.message &&
    typeof error.message === "string" &&
    !error.message.includes("non-2xx")
  ) {
    return error.message;
  }

  return fallback;
}
